import * as v from 'valibot';

/**
 * The MCP gateway: one governed entry point to every server reef can reach.
 *
 * Reef already routes every call through one bridge, so the governance half was
 * done. The half that was not is the *cost* of reachability: a step that mounts
 * six servers pays for six full tool catalogues, on every turn, whether or not
 * it calls any of them. Two hundred tools is a five-figure token bill per turn
 * for a step that used one of them, and the fix everybody reaches for first —
 * mounting fewer servers — is a capability cut dressed up as an optimisation.
 *
 * The gateway's answer is that a catalogue is a *search index*, not a manifest.
 * Above a threshold the step is handed one `discover` function instead of the
 * full surface; it asks for what it needs, gets back the handful of signatures
 * that match, and calls them exactly as before. Nothing about the policy engine,
 * the grant model or the audit trail changes — this is a rendering decision
 * about the surface, taken where the surface is generated.
 */

/** A tool as discovery returns it: enough to decide, not enough to call blind. */
export const GatewayToolMatch = v.object({
  mount: v.string(),
  tool: v.string(),
  /** The camelCase name the model actually writes. */
  method: v.string(),
  description: v.optional(v.string(), ''),
  classification: v.optional(v.nullable(v.picklist(['read', 'write'])), null),
  /** The rendered TypeScript signature, so a match is directly callable. */
  signature: v.string(),
});
export type GatewayToolMatch = v.InferOutput<typeof GatewayToolMatch>;

export const GatewayDiscoveryResult = v.object({
  query: v.string(),
  matches: v.array(GatewayToolMatch),
  /** Tools that matched but were not returned. Stated so a narrow query is a choice. */
  truncated: v.pipe(v.number(), v.integer()),
  /** Tools reachable on this step in total, matched or not. */
  available: v.pipe(v.number(), v.integer()),
});
export type GatewayDiscoveryResult = v.InferOutput<typeof GatewayDiscoveryResult>;

/**
 * What the gateway saved, per connection and in total.
 *
 * Measured in characters of rendered surface rather than in provider tokens,
 * and the estimate says so: a token count that depends on which tokenizer the
 * model happens to use would be a precise-looking number nobody could
 * reproduce. Four characters to a token is the industry rule of thumb and is
 * applied consistently, so the *ratio* — the thing the number is used for — is
 * honest even where the absolute is approximate.
 */
export const GatewayConnectionStat = v.object({
  connectionId: v.string(),
  name: v.string(),
  provider: v.string(),
  tools: v.pipe(v.number(), v.integer()),
  /** Characters the full declaration would render to. */
  fullChars: v.pipe(v.number(), v.integer()),
  /** Characters the discovery stub renders to instead. */
  discoveryChars: v.pipe(v.number(), v.integer()),
});
export type GatewayConnectionStat = v.InferOutput<typeof GatewayConnectionStat>;

export const GatewayStats = v.object({
  connections: v.pipe(v.number(), v.integer()),
  tools: v.pipe(v.number(), v.integer()),
  fullTokens: v.pipe(v.number(), v.integer()),
  discoveryTokens: v.pipe(v.number(), v.integer()),
  /** 0–1. What a step mounting everything saves per turn by discovering instead. */
  savedShare: v.number(),
  /** The tool count above which a mount is rendered as discovery. */
  threshold: v.pipe(v.number(), v.integer()),
  byConnection: v.optional(v.array(GatewayConnectionStat), []),
  computedAt: v.string(),
});
export type GatewayStats = v.InferOutput<typeof GatewayStats>;

/**
 * Tools on one mount above which the surface is rendered as discovery.
 *
 * Twelve, because below it the full declaration is both cheaper and better: the
 * model sees every signature without spending a turn asking, and a `discover`
 * round trip to find one of eight tools costs more than the eight cost to
 * declare. The threshold is the point where that flips, not a preference.
 */
export const GATEWAY_DISCOVERY_THRESHOLD = 12;

/** Matches per `discover` answer. Beyond this the query was too vague to act on. */
export const GATEWAY_DISCOVERY_LIMIT = 8;

/** Characters per token, for the estimate above. Stated once so every caller uses the same one. */
export const CHARS_PER_TOKEN = 4;
